import { supabase } from '../supabaseClient';

export const commissionRulesService = {
    async getAll(agencyId = null) {
        // Construir query base
        let query = supabase
            .from('commission_rules')
            .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, calculation_type, agent_value, agency_value, created_at, updated_at');

        // Si se proporciona agencyId, filtrar por él (permitir null para compatibilidad)
        if (agencyId) {
            query = query.eq('agency_id', agencyId);
        }

        // Aplicar ordenamiento (solo el último order se aplica, así que usamos múltiples campos)
        const { data, error } = await query
            .order('insurance_type', { ascending: true })
            .order('carrier', { ascending: true })
            .order('created_at', { ascending: false });

        if (error) {
            throw error;
        }
        return data || [];
    },

    // Alias for AgencyProcessor compatibility
    async getByAgency(agencyId) {
        return this.getAll(agencyId);
    },

    // Obtener reglas por tipo de seguro
    async getByInsuranceType(insuranceType) {
        const { data, error } = await supabase
            .from('commission_rules')
            .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, calculation_type, agent_value, agency_value, created_at, updated_at')
            .eq('insurance_type', insuranceType)
            .order('carrier', { ascending: true });
        if (error) {
            throw error;
        }
        return data || [];
    },

    // Obtener reglas por tipo de seguro y carrier
    async getByTypeAndCarrier(insuranceType, carrier) {
        const { data, error } = await supabase
            .from('commission_rules')
            .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, calculation_type, agent_value, agency_value, created_at, updated_at')
            .eq('insurance_type', insuranceType)
            .eq('carrier', carrier);
        if (error) {
            throw error;
        }
        return data || [];
    },

    async create(rule) {
        // Asegurar que agency_id esté presente
        if (!rule.agency_id) {
            throw new Error('agency_id es requerido para crear una regla de comisión');
        }

        // Validar calculation_type antes de insertar (asegurar minúsculas)
        const cleanRule = { ...rule };
        if (cleanRule.calculation_type) {
            cleanRule.calculation_type = cleanRule.calculation_type.toString().toLowerCase().trim();
            // Validar que sea un valor permitido
            if (!['percentage', 'fixed_per_member'].includes(cleanRule.calculation_type)) {
                cleanRule.calculation_type = 'percentage'; // Fallback seguro
            }
        } else {
            cleanRule.calculation_type = 'percentage'; // Valor por defecto
        }

        // Asegurar que los valores numéricos sean números válidos
        if (typeof cleanRule.agent_value !== 'number' || !isFinite(cleanRule.agent_value)) {
            cleanRule.agent_value = parseFloat(cleanRule.agent_value) || 0;
        }
        if (typeof cleanRule.agency_value !== 'number' || !isFinite(cleanRule.agency_value)) {
            cleanRule.agency_value = parseFloat(cleanRule.agency_value) || 0;
        }

        // Normalizar valores NULL para la búsqueda
        const normalizedState = cleanRule.state || null;
        const normalizedProduct = cleanRule.product || null;
        const normalizedAgentNpn = cleanRule.agent_npn || null;
        const normalizedTargetGroup = cleanRule.target_group || 'all';

        // BUSCAR si ya existe una regla con la misma combinación única
        let existingRuleQuery = supabase
            .from('commission_rules')
            .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, calculation_type, agent_value, agency_value, created_at, updated_at')
            .eq('agency_id', cleanRule.agency_id)
            .eq('insurance_type', cleanRule.insurance_type)
            .eq('carrier', cleanRule.carrier)
            .eq('target_group', normalizedTargetGroup)
            .eq('is_product_wildcard', cleanRule.is_product_wildcard || false);

        // Manejar campos NULL en la búsqueda
        if (normalizedState === null) {
            existingRuleQuery = existingRuleQuery.is('state', null);
        } else {
            existingRuleQuery = existingRuleQuery.eq('state', normalizedState);
        }

        if (normalizedProduct === null) {
            existingRuleQuery = existingRuleQuery.is('product', null);
        } else {
            existingRuleQuery = existingRuleQuery.eq('product', normalizedProduct);
        }

        if (normalizedAgentNpn === null) {
            existingRuleQuery = existingRuleQuery.is('agent_npn', null);
        } else {
            existingRuleQuery = existingRuleQuery.eq('agent_npn', normalizedAgentNpn);
        }

        const { data: existingRules, error: searchError } = await existingRuleQuery;

        if (searchError) {
            throw searchError;
        }

        // Si existe una regla, actualizarla
        if (existingRules && existingRules.length > 0) {
            const existingRule = existingRules[0];

            // Actualizar solo los campos que pueden cambiar
            const updateData = {
                agent_value: cleanRule.agent_value,
                calculation_type: cleanRule.calculation_type,
                agency_value: cleanRule.agency_value,
                updated_at: new Date().toISOString()
            };

            const { data: updatedData, error: updateError } = await supabase
                .from('commission_rules')
                .update(updateData)
                .eq('id', existingRule.id)
                .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, calculation_type, agent_value, agency_value, created_at, updated_at')
                .single();

            if (updateError) {
                throw updateError;
            }

            // Agregar flag para indicar que fue actualizado
            return { ...updatedData, _wasUpdated: true };
        }

        // Si no existe, crear nueva regla
        const { data, error } = await supabase
            .from('commission_rules')
            .insert([cleanRule])
            .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, calculation_type, agent_value, agency_value, created_at, updated_at')
            .single();
        if (error) {
            throw error;
        }

        // Agregar flag para indicar que fue creado
        return { ...data, _wasUpdated: false };
    },

    async update(id, updates) {
        // Excluir campos que no deberían actualizarse
        const { agency_id, ...updateableFields } = updates;

        // Filtrar campos undefined/null que pueden causar problemas
        const cleanUpdates = Object.fromEntries(
            Object.entries(updateableFields).filter(([_, value]) => value !== undefined)
        );

        // Si calculation_type no está en la BD, excluirlo del update
        // Esto evita errores 400 si la columna no existe
        if ('calculation_type' in cleanUpdates) {
            // Intentar incluir calculation_type, pero si falla, lo manejaremos
        }

        const { data, error } = await supabase
            .from('commission_rules')
            .update(cleanUpdates)
            .eq('id', id)
            .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, calculation_type, agent_value, agency_value, created_at, updated_at')
            .single();
        if (error) {

            // Si el error es por calculation_type, intentar sin ese campo
            if (error.code === '42703' || error.message?.includes('calculation_type') || error.message?.includes('column')) {
                const { calculation_type, ...updatesWithoutCalcType } = cleanUpdates;
                const { data: retryData, error: retryError } = await supabase
                    .from('commission_rules')
                    .update(updatesWithoutCalcType)
                    .eq('id', id)
                    .select('id, agency_id, target_group, insurance_type, agent_npn, carrier, product, is_product_wildcard, state, agent_value, agency_value, created_at, updated_at')
                    .single();
                if (retryError) {
                    throw retryError;
                }
                return retryData;
            }

            throw error;
        }
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('commission_rules')
            .delete()
            .eq('id', id);
        if (error) throw error;
    }
};
